import React, { useState, useEffect } from 'react';
import {
  createAdPlacement,
  deleteAdPlacement,
  getAdPlacementById,
  getAllAdPlacements,
  updateAdPlacement,
} from '../../services/AdManagement/AdService.js';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { FaEdit, FaEye } from 'react-icons/fa';
import { BsThreeDotsVertical } from 'react-icons/bs';

const AdPlacementForm = () => {
  const [adPlacements, setAdPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    adZoneConfiguration: '',
    responsiveView: 'Desktop',
    adPriority: 'High',
    adLink: '',
    adSize: '',
    adTitle: '',
    adName: '',
    adDescription: '',
    startDate: '',
    endDate: '',
    adBannerSize: '',
    image: '',
    enableReturningVisitors: false,
    enableWhileUsingSite: false,
    showToInteractedUsers: false,
    highPriorityAdsFirst: false,
  });
  const [editMode, setEditMode] = useState(false);
  const [currentAdId, setCurrentAdId] = useState(null);
  const [activePopoverId, setActivePopoverId] = useState(null);

  useEffect(() => {
    const fetchAdPlacements = async () => {
      try {
        setLoading(true);
        const data = await getAllAdPlacements();
        setAdPlacements(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch ad placements');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAdPlacements();
  }, []);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      setLoading(true);

      const formattedData = {
        ...formData,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      };

      if (editMode) {
        await updateAdPlacement(currentAdId, formattedData);
      } else {
        await createAdPlacement(formattedData);
      }

      const refreshedData = await getAllAdPlacements();
      setAdPlacements(refreshedData);

      resetForm();
      setError(null);
    } catch (err) {
      setError(editMode ? 'Failed to update ad placement' : 'Failed to create ad placement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async id => {
    try {
      setLoading(true);
      const adPlacement = await getAdPlacementById(id);

      const formattedStartDate = adPlacement.startDate ? adPlacement.startDate.split('T')[0] : '';
      const formattedEndDate = adPlacement.endDate ? adPlacement.endDate.split('T')[0] : '';

      setFormData({
        ...adPlacement,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      setEditMode(true);
      setCurrentAdId(id);
      setError(null);
    } catch (err) {
      setError('Failed to fetch ad placement details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this ad placement?')) {
      try {
        setLoading(true);
        await deleteAdPlacement(id);

        const refreshedData = await getAllAdPlacements();
        setAdPlacements(refreshedData);

        setError(null);
      } catch (err) {
        setError('Failed to delete ad placement');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };
  const handleView = ad => {
    console.log('Viewing ad:', ad);
  };

  const togglePopover = id => {
    setActivePopoverId(activePopoverId === id ? null : id);
  };

  const resetForm = () => {
    setFormData({
      adZoneConfiguration: '',
      responsiveView: 'Desktop',
      adPriority: 'High',
      adLink: '',
      adSize: '',
      adTitle: '',
      adName: '',
      adDescription: '',
      startDate: '',
      endDate: '',
      adBannerSize: '',
      image: '',
      enableReturningVisitors: false,
      enableWhileUsingSite: false,
      showToInteractedUsers: false,
      highPriorityAdsFirst: false,
    });
    setEditMode(false);
    setCurrentAdId(null);
  };

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  const getStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Unknown';

    const start = new Date(startDate);
    const end = new Date(endDate);

    return start >= end ? 'Expired' : 'Active';
  };

  return (
    <div className="font-nohemi">
      <div className="p-4">
        <h2 className="mb-4 text-base font-semibold">
          {editMode ? 'Edit Ad Placement' : 'Create New Ad Placement'}
        </h2>

        {error && <div className="p-3 mb-4 text-red-700 bg-red-100 rounded">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Left Column */}
            <div>
              <div className="mb-6">
                <label
                  htmlFor="adZoneConfiguration"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Zone Configuration<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="adZoneConfiguration"
                  name="adZoneConfiguration"
                  value={formData.adZoneConfiguration}
                  onChange={handleInputChange}
                  placeholder="Homepage, sidebar, footer, etc."
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block mb-2 text-sm font-bold text-gray-600">Ad Options</label>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="enableReturningVisitors"
                    name="enableReturningVisitors"
                    checked={formData.enableReturningVisitors}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <label
                    htmlFor="enableReturningVisitors"
                    className="text-sm font-bold text-gray-600">
                    Enable ads for returning visitors
                  </label>
                </div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="enableWhileUsingSite"
                    name="enableWhileUsingSite"
                    checked={formData.enableWhileUsingSite}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <label
                    htmlFor="enableWhileUsingSite"
                    className="text-sm font-bold text-gray-600">
                    Enable ads while using site
                  </label>
                </div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="showToInteractedUsers"
                    name="showToInteractedUsers"
                    checked={formData.showToInteractedUsers}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <label
                    htmlFor="showToInteractedUsers"
                    className="text-sm font-bold text-gray-600">
                    Show ads to users who previously interacted with the platform
                  </label>
                </div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="highPriorityAdsFirst"
                    name="highPriorityAdsFirst"
                    checked={formData.highPriorityAdsFirst}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <label
                    htmlFor="highPriorityAdsFirst"
                    className="text-sm font-bold text-gray-600">
                    High-priority ads appear first
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="adLink" className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Link<span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  id="adLink"
                  name="adLink"
                  value={formData.adLink}
                  onChange={handleInputChange}
                  placeholder="https://dummy.restapi.example.com/api/v1/..."
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="adTitle" className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Title<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="adTitle"
                  name="adTitle"
                  data-testid="ad-title"
                  value={formData.adTitle}
                  onChange={handleInputChange}
                  placeholder="Get 70% off discounts from Quelle"
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="mb-6">
                <label
                  htmlFor="responsiveView"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Responsive View<span className="text-red-500">*</span>
                </label>
                <select
                  id="responsiveView"
                  name="responsiveView"
                  value={formData.responsiveView}
                  onChange={handleInputChange}
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required>
                  <option value="Desktop">Desktop</option>
                  <option value="Tablet">Tablet</option>
                  <option value="Mobile">Mobile Preview</option>
                </select>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="adPriority"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Priority<span className="text-red-500">*</span>
                </label>
                <select
                  id="adPriority"
                  name="adPriority"
                  value={formData.adPriority}
                  onChange={handleInputChange}
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                  <option value="Very Low">Very Low</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="adSize" className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Size<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="adSize"
                  name="adSize"
                  value={formData.adSize}
                  onChange={handleInputChange}
                  placeholder="728x90"
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="adName" className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="adName"
                  name="adName"
                  value={formData.adName}
                  onChange={handleInputChange}
                  placeholder="Promotional ad5"
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>
          </div>

          {/* Description field */}
          <div className="mb-6">
            <label htmlFor="adDescription" className="block mb-2 text-sm font-bold text-gray-600">
              Ad Description<span className="text-red-500">*</span>
            </label>
            <textarea
              id="adDescription"
              name="adDescription"
              value={formData.adDescription}
              onChange={handleInputChange}
              placeholder="Description"
              className="w-full p-3 bg-[#F8F8F8] rounded-md text-gray-900 text-sm min-h-32"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Date fields */}
            <div>
              <div className="mb-6">
                <label htmlFor="startDate" className="block mb-2 text-sm font-bold text-gray-600">
                  Start Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="adBannerSize"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Ad Banner Size
                </label>
                <select
                  id="adBannerSize"
                  name="adBannerSize"
                  value={formData.adBannerSize}
                  onChange={handleInputChange}
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md">
                  <option value="">Select</option>
                  <option value="728x90">728x90</option>
                  <option value="300x50">300x50</option>
                  <option value="320x50">320x50</option>
                  <option value="468x60">468x60</option>
                  <option value="970x90">970x90</option>
                  <option value="300x250">300x250</option>
                  <option value="336x280">336x280</option>
                  <option value="970x250">970x250</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="image" className="block mb-2 text-sm font-bold text-gray-600">
                  Image URL
                </label>
                <input
                  type="url"
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <div className="mb-6">
                <label htmlFor="endDate" className="block mb-2 text-sm font-bold text-gray-600">
                  End Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="w-full p-3 text-sm text-gray-900 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            {editMode && (
              <button
                type="button"
                onClick={resetForm}
                className="text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-0 focus:outline-none font-light rounded-lg text-sm px-12 py-2.5 text-center">
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="text-white bg-black hover:bg-gray-800 focus:ring-0 focus:outline-none font-light rounded-lg text-sm px-12 py-2.5 text-center disabled:bg-gray-400">
              {loading ? 'Processing...' : editMode ? 'Update Ad' : 'Create Ad'}
            </button>
          </div>
        </form>

        <div className="mt-8">
          <h2 className="mb-2 text-sm font-medium">Ad Placements</h2>
          {loading && <div className="py-4 text-center">Loading...</div>}

          {!loading && adPlacements.length === 0 && (
            <div className="py-4 text-center text-gray-500">No ad placements found</div>
          )}

          {!loading && adPlacements.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full mt-4 border-collapse">
                <thead>
                  <tr className="text-left border-b bg-[#F5F6FA]">
                    <th className="p-2 font-light text-sm text-[#191B1C]">Title</th>
                    <th className="p-2 font-light text-sm text-[#191B1C]">Priority</th>
                    <th className="p-2 font-light text-sm text-[#191B1C]">Start Date/time</th>
                    <th className="p-2 font-light text-sm text-[#191B1C]">Ad Placement</th>
                    <th className="p-2 font-light text-sm text-[#191B1C]">Status</th>
                    <th className="p-2 font-light text-sm text-[#191B1C]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adPlacements.map((ad, index) => (
                    <tr key={ad._id || index + 1} className="border-b hover:bg-gray-100">
                      <td className="p-2 font-light text-sm text-[#191B1C] flex items-center space-x-2">
                        <img
                          src={ad.image}
                          className="object-cover w-8 h-8 rounded-full"
                          alt={ad.adName}
                        />
                        <span>{ad.adName || '-'}</span>
                      </td>
                      <td className="p-2 font-light text-sm text-[#191B1C]">{ad.adPriority}</td>
                      <td className="p-2 font-light text-sm text-[#191B1C]">
                        {formatDate(ad.startDate)},{' '}
                        {new Date(ad.startDate).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        }) || '-'}
                      </td>
                      <td className="p-2 font-light text-sm text-[#191B1C]">
                        {ad.adZoneConfiguration || '-'}
                      </td>
                      <td className="p-2 font-light text-[9px]">
                        <span
                          className={`px-2 py-1 rounded-sm ${
                            getStatus(ad.startDate, ad.endDate) === 'Expired'
                              ? 'bg-red-200 text-red-500'
                              : 'bg-green-200 text-[#00B69B]'
                          }`}>
                          {getStatus(ad.startDate, ad.endDate) || '-'}
                        </span>
                      </td>
                      <td className="relative w-8 h-8">
                        <button
                          className="focus:ring-0 focus:outline-none mx-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#F5F6F7]"
                          onClick={() => togglePopover(ad._id)}>
                          <BsThreeDotsVertical
                            className="text-[#191B1C] text-sm"
                            title="More options"
                          />
                        </button>

                        {activePopoverId === ad._id && (
                          <div
                            role="tooltip"
                            className="absolute z-10 flex flex-col w-40 p-3 text-sm text-gray-700 bg-white border border-gray-100 rounded-lg">
                            <button
                              aria-label="Edit"
                              className="inline-flex items-center text-[12px] px-3 py-2 hover:bg-gray-100 rounded"
                              onClick={() => handleEdit(ad._id)}>
                              <FaEdit className="mr-2 text-blue-600" />
                              Edit
                            </button>
                            <button
                              className="inline-flex items-center text-[12px] px-3 py-2 text-red-600 hover:bg-gray-100 rounded"
                              onClick={() => handleDelete(ad._id)}>
                              <RiDeleteBin5Line className="mr-2" />
                              Delete
                            </button>

                            <button
                              className="inline-flex items-center text-[12px] px-3 py-2 hover:bg-gray-100 rounded"
                              onClick={() => handleView(ad)}>
                              <FaEye className="mr-2 text-green-600" />
                              View
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdPlacementForm;
